import VideosApi from '../api/videos';

function getDefaultState() {
  return {
    supportedFileFormats: ['video/mp4', 'video/webm', 'video/quicktime'],
    maxFileSizeMB: 2560,
    minDurationMinutes: 2,
    maxDurationMinutes: 16,
    videoCount: 0,
    videos: [],
    loadingVideos: false,
    selectedVideo: null,
    uploadStatus: {
      uploading: false,
      progress: 0,
      videoRequest: null,
      thumbnailRequest: null,
      video: null,
    },
  };
}
export default {
  namespaced: true,
  state: getDefaultState(),
  getters: {
    allVideos(state) {
      return state.videos;
    },
    userVideos(state) {
      return state.videos;
    },
    videoCount(state) {
      return state.videoCount;
    },
    selectedVideo(state) {
      return state.selectedVideo;
    },
    constraints(state) {
      return {
        supportedFileFormats: state.supportedFileFormats,
        maxFileSizeMB: state.maxFileSizeMB,
        minDurationMinutes: state.minDurationMinutes,
        maxDurationMinutes: state.maxDurationMinutes,
      };
    },
    uploadStatus(state) {
      return state.uploadStatus;
    },
    loadingVideos(state) {
      return state.loadingVideos;
    },
  },
  actions: {
    uploadVideoMetadata(context, video) {
      return new Promise((resolve, reject) => {
        VideosApi.uploadVideoMetadata(video)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    uploadToGcp(context, { signedUrl, file, progressCallback }) {
      return new Promise((resolve, reject) => {
        context.commit('SET_UPLOAD_STATUS', {
          uploading: true,
          progress: 0,
        });
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
          // Return upload progress and XHR request with callback
          const p = parseInt(Math.round((e.loaded * 100) / e.total), 10);
          if (file.type.includes('video')) {
            progressCallback(p, xhr);
          } else {
            progressCallback(p, xhr);
          }
          context.commit('SET_UPLOAD_STATUS', {
            uploading: true,
            progress: p,
          });
        });
        xhr.open('PUT', signedUrl, true);
        xhr.onload = () => {
          if (xhr.status === 200) {
            // Upload finished succesfully
            context.commit('SET_UPLOAD_STATUS', {
              uploading: false,
              progress: 0,
            });
            resolve(true);
          } else {
            reject(new Error('Uploading failed!'));
          }
        };
        xhr.onerror = () => {
          reject(new Error('Uploading failed!'));
        };
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });
    },
    generateThumbnail(context, { preview }) {
      const videoElement = document.getElementById(preview);
      return new Promise((resolve, reject) => {
        if (videoElement == null) {
          reject();
          return;
        }
        const duration = videoElement.duration;
        videoElement.currentTime = Number.isFinite(duration) ? duration / 2.0 : 0;
        videoElement.addEventListener('seeked', () => {
          const canvas = document.createElement('canvas');
          canvas.width = videoElement.videoWidth;
          canvas.height = videoElement.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
          videoElement.currentTime = Number.isFinite(duration) ? duration : 0;
          ctx.canvas.toBlob((blob) => {
            resolve(blob);
          }, 'image/jpeg', 0.5);
        });
      });
    },
    async uploadVideo(context, { video, file, thumbnail }) {
      context.commit('SET_UPLOAD_STATUS', {
        uploading: true,
        progress: 0,
      });
      // Save metadata to backend
      let res;
      try {
        res = await context.dispatch('uploadVideoMetadata', video);
        // Set reference to video in case upload gets canceled
        context.commit('SET_UPLOAD_STATUS', { video: res.video });
      } catch (err) {
        context.commit('SET_UPLOAD_STATUS', {
          uploading: false,
          progress: 0,
        });
        console.log('Error uploading video', err);
        return null;
      }
      const videoUploadUrl = res.video_upload_url;
      const thumbnailUploadUrl = res.thumbnail_upload_url;
      let newVideo = { ...video, ...res.video }; // Update fields from backend
      console.log('upload url', res);
      // Upload to gcp
      const p1 = context.dispatch('uploadToGcp', {
        signedUrl: videoUploadUrl,
        file,
        progressCallback: (p, request) => {
          context.commit('SET_UPLOAD_STATUS', {
            uploading: true,
            progress: p,
            videoRequest: request,
          });
        },
      });
      const p2 = context.dispatch('uploadToGcp', {
        signedUrl: thumbnailUploadUrl,
        file: thumbnail,
        progressCallback: (_, request) => {
          context.commit('SET_UPLOAD_STATUS', { thumbnailRequest: request });
        },
      });
      try {
        await Promise.all([p1, p2]);
      } catch (err) {
        console.log('Error uploading video', err);
        context.commit('SET_UPLOAD_STATUS', {
          uploading: false,
          progress: 0,
        });

        return null;
      }
      // Notify backend
      newVideo = await context.dispatch('updateVideo', { ...newVideo, status: 'Uploaded' });
      context.commit('SET_UPLOAD_STATUS', {
        uploading: false,
        progress: 0,
      });
      console.log('Uploaded video successfully');
      if (newVideo.programsSharedWith.length > 0 || newVideo.usersSharedWith.length > 0) {
        console.log('share video');
        await context.dispatch('shareVideo',
          {
            video: newVideo,
            usersSharedWith: newVideo.usersSharedWith,
            programsSharedWith: newVideo.programsSharedWith,
          });
      }

      return newVideo;
    },
    async cancelUpload(context) {
      if (context.state.uploadStatus.uploading) {
        // Delete video metadata
        await context.dispatch('archiveVideo', context.state.uploadStatus.video);
        // Abort video and thumbnail upload
        if (context.state.uploadStatus.videoRequest) {
          await context.state.uploadStatus.videoRequest.abort();
        }
        if (context.state.uploadStatus.thumbnailRequest) {
          await context.state.uploadStatus.thumbnailRequest.abort();
        }
        context.commit('SET_UPLOAD_STATUS', {
          uploading: false,
          progress: 0,
        });
      }
    },
    getVideos(
      context,
      params = {
        created_by: null,
        visibility: 'SharedWithMe',
        offset: 0,
        limit: 25,
        sort: '-created_at',
        status: null,
      },
    ) {
      return new Promise((resolve, reject) => {
        context.commit('SET_LOADING_VIDEOS', true);
        VideosApi.getUserVideos(params)
          .then((response) => {
            const videos = response.result;

            context.commit('SET_VIDEOS', { videos, count: response.count });
            context.commit('SET_LOADING_VIDEOS', false);
            resolve(response);
          })
          .catch((error) => {
            context.commit('SET_LOADING_VIDEOS', false);
            reject(error);
          });
      });
    },
    getUserVideo(context, id) {
      return new Promise((resolve, reject) => {
        VideosApi.getUserVideo(id)
          .then((response) => {
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    async setSelectedVideo(context, video) {
      await context.commit('SET_SELECTED_VIDEO', video);
      await context.dispatch('videoAnalysis/getVideoScores', video._id, { root: true });
      await context.dispatch('configurations/getAllRecommendations', null, { root: true });
    },
    archiveVideo(context, video) {
      return new Promise((resolve, reject) => {
        VideosApi.archiveVideo(video)
          .then((response) => {
            context.commit('REMOVE_VIDEO', video);
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    updateVideo(context, video) {
      return new Promise((resolve, reject) => {
        VideosApi.updateVideo(video)
          .then(() => {
            context.commit('UPDATE_VIDEO', video);
            resolve(video);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    async generateSignedUrl({ commit }, { videoId, expiresIn }) {
      try {
        return await VideosApi.generateSignedUrl(videoId, expiresIn);
      } catch (error) {
        console.error('Error generating signed URL:', error);
        throw error;
      }
    },
    shareVideo(context, { video, usersSharedWith, programsSharedWith, visibility, sharedLinks }) {
      return new Promise((resolve, reject) => {
        VideosApi.shareVideo(video, usersSharedWith, programsSharedWith, visibility, sharedLinks)
          .then((res) => {
            context.commit('UPDATE_VIDEO', {
              ...video,
              users_shared_with: res.users_shared_with,
              programs_shared_with: res.programs_shared_with,
              visibility: res.visibility,
              shared_links: res.shared_links,
            });
            resolve(res.data);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    analyzeVideo(context, video) {
      return new Promise((resolve, reject) => {
        VideosApi.analyzeVideo(video)
          .then(async (response) => {
            context.commit('UPDATE_STATUS', { video, status: 'Analyzing' });
            resolve(response);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    resetState(context) {
      context.commit('RESET_STATE');
    },
  },
  mutations: {
    SET_VIDEOS: (state, { videos, count }) => {
      state.videos = videos;
      state.videoCount = count;
    },
    SET_SELECTED_VIDEO: (state, video) => {
      state.selectedVideo = video;
    },
    REMOVE_VIDEO: (state, video) => {
      const index = state.videos.findIndex(v => v._id === video._id);
      if (index === -1) return;
      state.videos.splice(index, 1);
      if (state.selectedVideo && state.selectedVideo._id === video._id) {
        state.selectedVideo = null;
      }
    },
    UPDATE_VIDEO: (state, video) => {
      if (state.selectedVideo && state.selectedVideo._id === video._id) {
        state.selectedVideo = video;
      }

      const index = state.videos.findIndex(v => v._id === video._id);
      if (index === -1) return;
      state.videos[index] = video;
    },
    RESET_STATE: (state) => {
      Object.assign(state, getDefaultState());
    },
    UPDATE_STATUS: (state, { video, status }) => {
      const index = state.videos.findIndex(v => v._id === video._id);
      if (index === -1) return;
      state.videos[index].status = status;
    },
    SET_UPLOAD_STATUS: (state, {
      uploading, progress, videoRequest, thumbnailRequest, video,
    }) => {
      state.uploadStatus.uploading = !!uploading;
      if (progress) state.uploadStatus.progress = progress;
      if (videoRequest) state.uploadStatus.videoRequest = videoRequest;
      if (thumbnailRequest) state.uploadStatus.thumbnailRequest = thumbnailRequest;
      if (video) state.uploadStatus.video = video;
    },
    SET_LOADING_VIDEOS: (state, loading) => {
      state.loadingVideos = loading;
    },
  },
};
